// Reports data for the tenant workspace (mockup 24 — "Reportes"). The index: the
// current reporting scope (bot · period · channel) plus six perspectives on the
// operation, each with its headline metric. The full per-report drill-downs
// (view 2 — charts, heatmaps, breakdowns) are a follow-up.
//
// Wired: getReports()/getReportDetail() -> GET /reports +
// /reports/:id, computed tenant-scoped from conversations + leads + team. The
// index headlines + each report's KPIs/breakdowns are real; the daily-trend
// chart, extra breakdown dimensions, and the SLA/knowledge measurements are
// incremental follow-ups (those reports currently read "—").

import { isAxiosError } from 'axios'

import { apiClient } from '../../api/client.js'

export interface ReportScope {
  bot: string
  botHint: string
  period: string
  periodHint: string
  channel: string
  channelHint: string
}

export type ReportId =
  | 'conversaciones'
  | 'prospectos'
  | 'equipo'
  | 'tiempos'
  | 'conocimiento'
  | 'citas'
export type DeltaTone = 'up' | 'warn' | 'neutral'

export interface ReportSummary {
  id: ReportId
  title: string
  description: string
  value: string
  unit?: string
  statLabel: string
  delta: { text: string; tone: DeltaTone }
  /** Vertical-specific report (e.g. only hospitals see "Citas"). */
  verticalBadge?: string
}

export interface ReportsData {
  scope: ReportScope
  reports: ReportSummary[]
}

/** A reporting window. '' / unknown falls back to 30 days on the server. */
export type ReportPeriod = '7d' | '30d' | '90d'

export const REPORT_PERIODS: { value: ReportPeriod; label: string }[] = [
  { value: '7d', label: 'Últimos 7 días' },
  { value: '30d', label: 'Últimos 30 días' },
  { value: '90d', label: 'Últimos 90 días' },
]

/**
 * GET /reports — the index: scope (bot · period · channel) + the six report
 * headlines, computed tenant-scoped from conversations, leads and team.
 */
export async function getReports(period: ReportPeriod = '30d'): Promise<ReportsData> {
  const res = await apiClient.get<{ data: ReportsData }>('/reports', { params: { period } })
  return res.data.data
}

// --- Report drill-down (mockup 24, view 2) ------------------------------------

export interface ReportKpi {
  label: string
  value: string
  unit?: string
  delta?: { text: string; tone: DeltaTone }
}
/** A daily trend, this period vs the previous one. */
export interface ReportTrend {
  title: string
  sub: string
  yMax: number
  xLabels: string[]
  thisPeriod: number[]
  lastPeriod: number[]
}
export interface BreakdownItem {
  label: string
  /** Tailwind colour class for the bar + dot. */
  color: string
  value: number
  pct: number
}
export interface Breakdown {
  title: string
  meta?: string
  /** 'count' renders «N · P%»; 'rank' renders just the value with a rank index. */
  items: BreakdownItem[]
}
export interface ReportDetail {
  id: ReportId
  title: string
  scope: ReportScope
  filters: string[]
  kpis: ReportKpi[]
  trend?: ReportTrend
  breakdowns: Breakdown[]
}

/**
 * GET /reports/:id — one report's drill-down (KPIs, trend, breakdowns).
 * Null on 404 (unknown report id), rethrows other errors.
 */
export async function getReportDetail(
  id: string,
  period: ReportPeriod = '30d',
): Promise<ReportDetail | null> {
  try {
    const res = await apiClient.get<{ data: ReportDetail }>(`/reports/${id}`, {
      params: { period },
    })
    return res.data.data
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}
